import axios from "axios";
import { ref } from "@vue/composition-api";

// Instance Axios séparée SANS intercepteur d'authentification
const companyApi = JSON.parse(localStorage.getItem("COMPANY_INFO"));

const publicAxios = axios.create({
  baseURL: process.env[`VUE_APP_API_${companyApi?.name}_BASE_URL`],
  headers: {
    Accept: "application/vnd.api+json",
    "Content-Type": "application/vnd.api+json",
  },
});

export default function useQuoteValidationPublic() {
  const isLoading = ref(false);
  const sessionToken = ref(null);
  const budgetTracking = ref(null);
  const opportunityReference = ref(null);
  const errorMessage = ref(null);

  /**
   * Vérifie le code OTP
   * @param {string} token - Token de validation
   * @param {string} code - Code OTP 6 chiffres
   * @returns {Promise}
   */
  const verifyCode = async (token, code) => {
    isLoading.value = true;
    errorMessage.value = null;

    try {
      const { data: { resource }, status } = await publicAxios.post(
        `/quote-validation/${token}/verify-code`,
        { code }
      );

      if (status === 200) {
        sessionToken.value = resource.session_token;
        budgetTracking.value = resource.budget_tracking;
        opportunityReference.value = resource.opportunity_reference;
        return resource;
      }
    } catch (error) {
      errorMessage.value =
        error.response?.data?.message || "Une erreur est survenue lors de la validation de votre code.";
      throw error;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Approuve le devis
   * @param {string} token - Token de validation
   * @returns {Promise}
   */
  const approve = async (token) => {
    if (!sessionToken.value) {
      throw new Error("Session non initialisée. Veuillez vérifier le code OTP.");
    }

    isLoading.value = true;
    errorMessage.value = null;

    try {
      const { data, status } = await publicAxios.post(
        `/quote-validation/${token}/approve`,
        {},
        {
          headers: {
            Authorization: `Bearer ${sessionToken.value}`,
          },
        }
      );

      if (status === 200) {
        return data;
      }
    } catch (error) {
      errorMessage.value =
        error.response?.data?.message || "Erreur lors de l'approbation du devis";
      throw error;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Rejette le devis avec un commentaire
   * @param {string} token - Token de validation
   * @param {string} comment - Commentaire de refus (min 5 caractères)
   * @returns {Promise}
   */
  const reject = async (token, comment) => {
    if (!sessionToken.value) {
      throw new Error("Session non initialisée. Veuillez vérifier le code OTP.");
    }

    if (!comment || comment.length < 5) {
      throw new Error("Le commentaire doit contenir au moins 5 caractères");
    }

    isLoading.value = true;
    errorMessage.value = null;

    try {
      const { data, status } = await publicAxios.post(
        `/quote-validation/${token}/reject`,
        { comment },
        {
          headers: {
            Authorization: `Bearer ${sessionToken.value}`,
          },
        }
      );

      if (status === 200) {
        return data;
      }
    } catch (error) {
      errorMessage.value =
        error.response?.data?.message || "Erreur lors du rejet du devis";
      throw error;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Réinitialise l'état
   */
  const reset = () => {
    sessionToken.value = null;
    budgetTracking.value = null;
    opportunityReference.value = null;
    errorMessage.value = null;
    isLoading.value = false;
  };

  return {
    isLoading,
    sessionToken,
    budgetTracking,
    opportunityReference,
    errorMessage,
    verifyCode,
    approve,
    reject,
    reset,
  };
}
